import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_URL } from '../../config/environment';
import api from '../../services/api';

// Domyślne usługi wysyłki (fallback)
const DEFAULT_SERVICES = [
  { id: 'inpost_locker_standard', name: 'Paczkomat → Paczkomat', description: 'Nadanie w paczkomacie, odbiór w paczkomacie', icon: '📦', requires_point: true, requires_dropoff: false },
  { id: 'inpost_locker_to_address', name: 'Paczkomat → Kurier', description: 'Nadanie w paczkomacie, dostawa kurierem na adres', icon: '📦🚚', requires_point: false, requires_dropoff: true, requires_address: true },
  { id: 'inpost_courier_standard', name: 'Kurier → Adres', description: 'Odbiór kurierem, dostawa pod adres', icon: '🚚', requires_point: false, requires_dropoff: false },
  { id: 'odbior_osobisty', name: 'Odbiór osobisty', description: 'Odbiór w sklepie', icon: '🏪', requires_point: false, requires_dropoff: false }
];

function ShippingSelector({
  selectedMethod,
  selectedPoint,
  onMethodChange,
  onPointChange,
  receiverAddress,
  orderId,  // ID zamówienia do wystawienia etykiety
  onCreateLabel,  // Callback po wygenerowaniu etykiety
  customerPhone,  // Telefon klienta z zamówienia
  customerEmail   // Email klienta z zamówienia
}) {
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [parcelTemplates, setParcelTemplates] = useState([]);
  const [points, setPoints] = useState([]);
  const [searchCity, setSearchCity] = useState('');
  const [searchPostCode, setSearchPostCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPointPicker, setShowPointPicker] = useState(false);
  const [showDropoffPicker, setShowDropoffPicker] = useState(false);
  const [creatingLabel, setCreatingLabel] = useState(false);

  // Paczkomat nadawczy (dla Paczkomat → Kurier)
  const [dropoffPoint, setDropoffPoint] = useState(null);

  // Dane do etykiety (jeśli brakuje u klienta)
  const [labelPhone, setLabelPhone] = useState('');
  const [labelEmail, setLabelEmail] = useState('');

  useEffect(() => {
    fetchServices();
    fetchParcelTemplates();
  }, []);
  
  // Ustaw domyślne wartości z danych klienta
  useEffect(() => {
    if (customerPhone && !labelPhone) setLabelPhone(customerPhone);
    if (customerEmail && !labelEmail) setLabelEmail(customerEmail);
  }, [customerPhone, customerEmail]);

  useEffect(() => {
    // Automatycznie pokaż picker punktu jeśli wybrano paczkomat docelowy
    if (selectedMethod === 'inpost_locker_standard') {
      setShowPointPicker(true);
      setShowDropoffPicker(false);
    } else if (selectedMethod === 'inpost_locker_to_address') {
      // Paczkomat → Kurier: pokaż picker paczkomatu nadawczego
      setShowPointPicker(false);
      setShowDropoffPicker(true);
    } else {
      setShowPointPicker(false);
      setShowDropoffPicker(false);
    }
  }, [selectedMethod]);

  const fetchServices = async () => {
    try {
      const response = await api.get('/shipping/services');
      const data = response.data;
      if (data.success && data.data && data.data.length > 0) {
        setServices(data.data);
      }
    } catch (error) {
      console.error('Błąd pobierania usług:', error);
    }
  };

  const fetchParcelTemplates = async () => {
    try {
      const response = await api.get('/shipping/parcel-templates');
      const data = response.data;
      if (data.success) {
        setParcelTemplates(data.data);
      }
    } catch (error) {
      console.error('Błąd pobierania szablonów:', error);
    }
  };

  const searchPoints = async () => {
    if (!searchCity && !searchPostCode) {
      toast.error('Wprowadź miasto lub kod pocztowy');
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchCity) params.append('city', searchCity);
      if (searchPostCode) params.append('post_code', searchPostCode);
      params.append('type', 'parcel_locker');
      
      const response = await api.get(`/shipping/points?${params.toString()}`);
      const data = response.data;
      if (data.success) {
        setPoints(data.data.items || []);
      }
    } catch (error) {
      console.error('Błąd wyszukiwania punktów:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPoint = (point) => {
    const pointData = {
      name: point.name,
      address: point.address?.line1 || '',
      city: point.address?.city || '',
      post_code: point.address?.post_code || ''
    };
    onPointChange(pointData);
    setShowPointPicker(false);
  };

  // Wybór paczkomatu nadawczego (dla Paczkomat → Kurier)
  const handleSelectDropoffPoint = (point) => {
    const pointData = {
      name: point.name,
      address: point.address?.line1 || '',
      city: point.address?.city || '',
      post_code: point.address?.post_code || ''
    };
    setDropoffPoint(pointData);
    setShowDropoffPicker(false);
  };

  // Funkcja generowania etykiety InPost
  const handleCreateLabel = async () => {
    if (!orderId) {
      toast.error('Najpierw zapisz zamówienie');
      return;
    }

    // Sprawdź wymagane dane
    const phone = labelPhone || customerPhone;
    const email = labelEmail || customerEmail;

    if (!phone) {
      toast.error('Podaj numer telefonu odbiorcy');
      return;
    }
    if (!email) {
      toast.error('Podaj email odbiorcy');
      return;
    }
    if (selectedMethod === 'inpost_locker_standard' && !selectedPoint?.name) {
      toast.error('Wybierz paczkomat docelowy');
      return;
    }
    if (selectedMethod === 'inpost_locker_to_address' && !dropoffPoint?.name) {
      toast.error('Wybierz paczkomat nadawczy');
      return;
    }
    if (selectedMethod === 'inpost_locker_to_address' && !receiverAddress?.street) {
      toast.error('Brak adresu odbiorcy - wymagany dla dostawy kurierem');
      return;
    }

    setCreatingLabel(true);
    try {
      // Przygotuj dane do wysyłki
      const shipmentData = {
        order_id: orderId,
        service: selectedMethod,
        receiver_phone: phone,
        receiver_email: email
      };

      // Dla Paczkomat → Paczkomat: target_point
      if (selectedMethod === 'inpost_locker_standard') {
        shipmentData.target_point = selectedPoint?.name;
      }

      // Dla Paczkomat → Kurier: dropoff_point + adres odbiorcy
      if (selectedMethod === 'inpost_locker_to_address') {
        shipmentData.dropoff_point = dropoffPoint?.name;
        shipmentData.receiver_street = receiverAddress?.street;
        shipmentData.receiver_city = receiverAddress?.city;
        shipmentData.receiver_post_code = receiverAddress?.post_code;
      }

      // Dla Kurier → Adres: adres odbiorcy
      if (selectedMethod === 'inpost_courier_standard') {
        shipmentData.receiver_street = receiverAddress?.street;
        shipmentData.receiver_city = receiverAddress?.city;
        shipmentData.receiver_post_code = receiverAddress?.post_code;
      }

      const response = await api.post('/shipping/shipments', shipmentData);
      const data = response.data;

      if (data.success) {
        toast.success(`Etykieta wygenerowana! Numer przesyłki: ${data.tracking_number || 'W trakcie opłacania'}`);
        if (onCreateLabel) onCreateLabel(data.data);
      } else {
        toast.error(`Błąd: ${data.message}`);
      }
    } catch (error) {
      console.error('Błąd generowania etykiety:', error);
      toast.error('Błąd generowania etykiety');
    } finally {
      setCreatingLabel(false);
    }
  };

  // Pobierz nazwę usługi
  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service ? service.name : serviceId;
  };

  return (
    <div style={{ 
      border: '1px solid var(--border)', 
      borderRadius: '8px', 
      padding: '1rem',
      background: 'var(--bg)'
    }}>
      <h4 style={{ margin: '0 0 1rem', fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        🚚 Metoda dostawy
      </h4>

      {/* Wybór usługi */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        {services.map(service => (
          <button
            key={service.id}
            type="button"
            onClick={() => onMethodChange(service.id)}
            style={{
              padding: '6px 12px',
              border: selectedMethod === service.id ? '2px solid var(--info)' : '1px solid var(--border)',
              background: selectedMethod === service.id ? 'var(--info-light)' : 'var(--card-bg)' ,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '12px'
            }}
          >
            <span style={{ fontSize: '16px' }}>{service.icon}</span>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontWeight: '600' }}>{service.name}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{service.description}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Picker paczkomatu */}
      {showPointPicker && (
        <div style={{ 
          border: '1px solid var(--border)', 
          borderRadius: '6px', 
          padding: '0.75rem',
          background: 'var(--card-bg)'
        }}>
          <h5 style={{ margin: '0 0 0.75rem', fontSize: '12px', color: 'var(--text-secondary)' }}>
            📦 Wybierz paczkomat
          </h5>

          {/* Wybrany punkt - DUŻE I WYRAŹNE */}
          {selectedPoint && (
            <div style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, var(--success) 0%, var(--success) 100%)',
              borderRadius: '8px',
              marginBottom: '0.75rem',
              color: '#fff' ,
              boxShadow: '0 2px 8px rgba(40, 167, 69, 0.3)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '10px', opacity: 0.9, marginBottom: '4px' }}>
                    ✅ WYBRANY PACZKOMAT
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>
                    📦 {selectedPoint.name}
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.95 }}>
                    {selectedPoint.address}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>
                    {selectedPoint.post_code} {selectedPoint.city}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    onPointChange(null);
                    setShowPointPicker(true);
                  }}
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    padding: '4px 10px'
                  }}
                >
                  Zmień
                </button>
              </div>
            </div>
          )}

          {/* Wyszukiwanie */}
          {!selectedPoint && (
            <>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <input
                  type="text"
                  value={searchCity}
                  onChange={e => setSearchCity(e.target.value)}
                  placeholder="Miasto"
                  style={{
                    flex: 2,
                    padding: '6px 10px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text)',
                    fontSize: '12px'
                  }}
                />
                <input
                  type="text"
                  value={searchPostCode}
                  onChange={e => setSearchPostCode(e.target.value)}
                  placeholder="Kod pocztowy"
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text)',
                    fontSize: '12px'
                  }}
                />
                <button
                  type="button"
                  onClick={searchPoints}
                  disabled={loading}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid var(--info)', color: 'var(--info)' ,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '12px'
                  }}
                >
                  {loading ? '...' : '🔍'}
                </button>
              </div>

              {/* Lista punktów */}
              {points.length > 0 && (
                <div style={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  border: '1px solid var(--border)',
                  borderRadius: '4px'
                }}>
                  {points.map(point => (
                    <div
                      key={point.name}
                      onClick={() => handleSelectPoint(point)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        fontSize: '11px',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={e => e.target.style.background = 'var(--bg)'}
                      onMouseLeave={e => e.target.style.background = 'white'}
                    >
                      <strong style={{ color: 'var(--warning)' }}>📦 {point.name}</strong>
                      <br />
                      <span style={{ color: 'var(--text-muted)' }}>
                        {point.address?.line1}, {point.address?.post_code} {point.address?.city}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {points.length === 0 && searchCity && !loading && (
                <div style={{ 
                  padding: '1rem', 
                  textAlign: 'center', 
                  color: 'var(--text-muted)',
                  fontSize: '12px' 
                }}>
                  Nie znaleziono paczkomatów. Spróbuj innego miasta.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Picker paczkomatu NADAWCZEGO (dla Paczkomat → Kurier) */}
      {showDropoffPicker && (
        <div style={{
          border: '1px solid var(--border)',
          borderRadius: '6px',
          padding: '0.75rem',
          background: 'var(--card-bg)',
          marginTop: '0.5rem'
        }}>
          <h5 style={{ margin: '0 0 0.75rem', fontSize: '12px', color: 'var(--text-secondary)' }}>
            📦 Wybierz paczkomat NADAWCZY (gdzie wrzucisz paczkę)
          </h5>

          {/* Wybrany paczkomat nadawczy */}
          {dropoffPoint && (
            <div style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, var(--info) 0%, var(--purple) 100%)',
              borderRadius: '8px',
              marginBottom: '0.75rem',
              color: '#fff' ,
              boxShadow: '0 2px 8px rgba(13, 110, 253, 0.3)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '10px', opacity: 0.9, marginBottom: '4px' }}>
                    ✅ PACZKOMAT NADAWCZY
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>
                    📦 {dropoffPoint.name}
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.95 }}>
                    {dropoffPoint.address}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>
                    {dropoffPoint.post_code} {dropoffPoint.city}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setDropoffPoint(null);
                    setShowDropoffPicker(true);
                  }}
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    padding: '4px 10px'
                  }}
                >
                  Zmień
                </button>
              </div>
            </div>
          )}

          {/* Wyszukiwanie */}
          {!dropoffPoint && (
            <>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <input
                  type="text"
                  value={searchCity}
                  onChange={e => setSearchCity(e.target.value)}
                  placeholder="Miasto"
                  style={{
                    flex: 2,
                    padding: '6px 10px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text)',
                    fontSize: '12px'
                  }}
                />
                <input
                  type="text"
                  value={searchPostCode}
                  onChange={e => setSearchPostCode(e.target.value)}
                  placeholder="Kod pocztowy"
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text)',
                    fontSize: '12px'
                  }}
                />
                <button
                  type="button"
                  onClick={searchPoints}
                  disabled={loading}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid var(--info)', color: 'var(--info)' ,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '12px'
                  }}
                >
                  {loading ? '...' : '🔍'}
                </button>
              </div>

              {/* Lista punktów */}
              {points.length > 0 && (
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid var(--border)',
                  borderRadius: '4px'
                }}>
                  {points.map(point => (
                    <div
                      key={point.name}
                      onClick={() => handleSelectDropoffPoint(point)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        fontSize: '11px',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={e => e.target.style.background = 'var(--bg)'}
                      onMouseLeave={e => e.target.style.background = 'white'}
                    >
                      <strong style={{ color: 'var(--info)' }}>📦 {point.name}</strong>
                      <br />
                      <span style={{ color: 'var(--text-muted)' }}>
                        {point.address?.line1}, {point.address?.post_code} {point.address?.city}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {points.length === 0 && searchCity && !loading && (
                <div style={{
                  padding: '1rem',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: '12px'
                }}>
                  Nie znaleziono paczkomatów. Spróbuj innego miasta.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Info dla kuriera / adres dostawy */}
      {selectedMethod && (selectedMethod === 'inpost_locker_to_address' || selectedMethod === 'inpost_courier_standard') && receiverAddress && (
        <div style={{
          padding: '0.5rem 0.75rem',
          background: 'var(--info-light)',
          borderRadius: '4px',
          fontSize: '12px',
          marginTop: '0.5rem'
        }}>
          <strong>📍 Adres dostawy (kurier):</strong><br />
          {receiverAddress.street}, {receiverAddress.post_code} {receiverAddress.city}
        </div>
      )}

      {/* PODSUMOWANIE WYSYŁKI - pokazuj gdy metoda wybrana */}
      {selectedMethod && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          background: 'var(--warning-light)',
          border: '1px solid var(--warning)',
          borderRadius: '6px'
        }}>
          <div style={{ fontSize: '11px', color: 'var(--warning)', marginBottom: '0.5rem' }}>
            📋 <strong>PODSUMOWANIE WYSYŁKI</strong>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text)' }}>
            <div><strong>Metoda:</strong> {getServiceName(selectedMethod)}</div>
            {/* Paczkomat docelowy dla Paczkomat → Paczkomat */}
            {selectedMethod === 'inpost_locker_standard' && selectedPoint && (
              <div><strong>Paczkomat odbioru:</strong> {selectedPoint.name} ({selectedPoint.city})</div>
            )}
            {/* Paczkomat nadawczy dla Paczkomat → Kurier */}
            {selectedMethod === 'inpost_locker_to_address' && dropoffPoint && (
              <div><strong>Paczkomat nadania:</strong> {dropoffPoint.name} ({dropoffPoint.city})</div>
            )}
            {/* Adres dla dostaw kurierem */}
            {(selectedMethod === 'inpost_locker_to_address' || selectedMethod === 'inpost_courier_standard') && receiverAddress && (
              <div><strong>Adres dostawy:</strong> {receiverAddress.street}, {receiverAddress.city}</div>
            )}
          </div>

          {/* Dane kontaktowe do etykiety InPost */}
          {orderId && (selectedMethod === 'inpost_locker_standard' || selectedMethod === 'inpost_locker_to_address' || selectedMethod === 'inpost_courier_standard') && (
            <div style={{ 
              marginTop: '0.75rem', 
              padding: '0.5rem', 
              background: 'rgba(255,255,255,0.7)', 
              borderRadius: '4px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                📞 Dane kontaktowe odbiorcy (wymagane przez InPost):
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <input
                  type="tel"
                  value={labelPhone}
                  onChange={(e) => setLabelPhone(e.target.value)}
                  placeholder="Telefon odbiorcy *"
                  style={{
                    flex: 1,
                    minWidth: '120px',
                    padding: '6px 10px',
                    border: labelPhone ? '1px solid var(--success)' : '1px solid var(--danger)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text)',
                    fontSize: '12px'
                  }}
                />
                <input
                  type="email"
                  value={labelEmail}
                  onChange={(e) => setLabelEmail(e.target.value)}
                  placeholder="Email odbiorcy *"
                  style={{
                    flex: 1,
                    minWidth: '150px',
                    padding: '6px 10px',
                    border: labelEmail ? '1px solid var(--success)' : '1px solid var(--danger)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text)',
                    fontSize: '12px'
                  }}
                />
              </div>
            </div>
          )}
          
          {/* Przycisk etykiety - dla InPost (wszystkie usługi) */}
          {orderId &&
           (selectedMethod === 'inpost_locker_standard' || selectedMethod === 'inpost_locker_to_address' || selectedMethod === 'inpost_courier_standard') &&
           (selectedMethod !== 'inpost_locker_standard' || selectedPoint) &&
           (selectedMethod !== 'inpost_locker_to_address' || dropoffPoint) && (
            <button
              type="button"
              onClick={handleCreateLabel}
              disabled={creatingLabel || !labelPhone || !labelEmail}
              style={{
                marginTop: '0.75rem',
                padding: '6px 12px',
                background: creatingLabel || !labelPhone || !labelEmail ? 'var(--text-muted)' : 'var(--warning)',
                color: '#000',
                cursor: (creatingLabel || !labelPhone || !labelEmail) ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '12px',
                width: '100%'
              }}
            >
              {creatingLabel ? '⏳ Generowanie...' : '🏷️ Wygeneruj etykietę InPost'}
            </button>
          )}

          {/* Info dla paczkomatu bez wybranego punktu */}
          {orderId && selectedMethod === 'inpost_locker_standard' && !selectedPoint && (
            <div style={{
              marginTop: '0.5rem',
              fontSize: '10px',
              color: 'var(--warning)',
              fontStyle: 'italic'
            }}>
              💡 Wybierz paczkomat docelowy, aby wygenerować etykietę
            </div>
          )}

          {/* Info dla Paczkomat → Kurier bez wybranego dropoff */}
          {orderId && selectedMethod === 'inpost_locker_to_address' && !dropoffPoint && (
            <div style={{
              marginTop: '0.5rem',
              fontSize: '10px',
              color: 'var(--warning)',
              fontStyle: 'italic'
            }}>
              💡 Wybierz paczkomat nadawczy, aby wygenerować etykietę
            </div>
          )}

          {/* Info gdy zamówienie nie zapisane */}
          {!orderId && (selectedMethod === 'inpost_locker_standard' || selectedMethod === 'inpost_locker_to_address' || selectedMethod === 'inpost_courier_standard') && (
            <div style={{
              marginTop: '0.5rem',
              fontSize: '10px',
              color: 'var(--warning)',
              fontStyle: 'italic'
            }}>
              💡 Zapisz zamówienie, aby wygenerować etykietę
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ShippingSelector;
